import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Appbar } from '@Components/app-bar';
import { Button, Box, styled } from '@mui/material';
import Table from '@Components/table';
import useActionColumn from '@Components/table/useActionColumn';
import { Modal } from '@Components/modal';
import useModal from 'hooks/useModal';
import { toast } from 'react-toastify';
import { cultureColumns } from './columns';
import { AddCultureForm } from './add-culture';
import AddNewIcon from '../../assets/icons/add-new-user-icon.svg';
import { useCulturesQuery, useDeleteCultureMutation } from '@Store/api/translationsSlice';
import { ICulture } from '@Types/Translation';
import { LOCALIZATION_PAGE } from '@Routes/constants';

const Container = styled(Box)`
  padding: 0px;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
`;

const AddNewCultureButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.secondary.main,
  padding: '18px 20px',
  borderRadius: 30,
  color: '#FFF',
  img: {
    marginRight: 10,
  },
  textTransform: 'capitalize',
  '&:hover': { backgroundColor: theme.palette.secondary.main },
}));

export function Localization() {
  const { data: cultures } = useCulturesQuery();
  const [deleteCulture] = useDeleteCultureMutation();
  const navigate = useNavigate();

  const {
    isOpen: isAddCultureModalOpen,
    openModal: openAddCultureModal,
    closeModal: closeAddCultureModal,
  } = useModal<ICulture>();

  const handleOpenEditTranslations = useCallback(
    (culture: ICulture) => {
      navigate(`${LOCALIZATION_PAGE}/culture/${culture.id}`);
    },
    [navigate]
  );

  const handleDeleteCulture = useCallback(
    async (culture: ICulture) => {
      if (culture.isBaseline) {
        toast.error('The baseline culture cannot be deleted');

        return;
      }

      try {
        await deleteCulture(culture.id).unwrap();
        toast.success('Culture deleted');
      } catch (err: any) {
        const message =
          err?.data?.message || err?.message || 'Something went wrong...';
        toast.error(message);
      }
    },
    [deleteCulture]
  );

  const actionColumn = useActionColumn<ICulture>({
    handleEdit: handleOpenEditTranslations,
    handleDelete: handleDeleteCulture,
    shouldDisableDelete: (culture) => culture.isBaseline,
  });

  return (
    <Container>
      <Appbar searchable title={'Localization'}>
        <AddNewCultureButton onClick={() => openAddCultureModal()}>
          <img alt='Add New' src={AddNewIcon} />
          Add Culture
        </AddNewCultureButton>
      </Appbar>
      <Table
        columns={[...cultureColumns, actionColumn]}
        paginated={false}
        rows={cultures || []}
        totalCount={cultures?.length || 0}
      />

      {isAddCultureModalOpen ? (
        <Modal
          handleClose={closeAddCultureModal}
          label='Add new culture'
          open={isAddCultureModalOpen}
        >
          <AddCultureForm handleClose={closeAddCultureModal} />
        </Modal>
      ) : null}
    </Container>
  );
}
